import React, { useEffect, useState } from "react";
import { Modal, ModalHeader, ModalBody, ModalFooter } from "reactstrap";
import { Multiselect } from "multiselect-react-dropdown";
import { EditorState, ContentState, convertToRaw } from "draft-js";
import { Editor } from "react-draft-wysiwyg";
import htmlToDraft from "html-to-draftjs";
import draftToHtml from "draftjs-to-html";
import "react-draft-wysiwyg/dist/react-draft-wysiwyg.css";
import ConfirmationEmailModal from "./ConfirmationEmailModal";
import ImportModulePresetModal from "./ImportModulePresetModal";
import { API_URL, PRODUCT_S3 } from "../../../config";

const ADD_DEVICE_ENDPOINT = `${API_URL}/distributor/product/add`;
const DEVICE_CATEGORY = 22;
const EXCLUDED_MODULE_ENUMS = ["REPORT", "BASIC"];

const ADVANCED_MODULE_ENUMS = [
  "ADVANCED_FEATURES_MODULE",
  "CELL_PHONE_MODULE",
  "PRACTICUMS_MODULE",
  "CLIENTS_MODULE",
];
const PROFESSIONAL_MODULE_ENUMS = [
  "ADVANCED_FEATURES_MODULE",
  "CELL_PHONE_MODULE",
  "PRACTICUMS_MODULE",
  "CLIENTS_MODULE",
  "MANAGEMENT_MODULE",
  "TRAINER_MODULE",
  "HRV_MODULE",
];
const GROUP_SYSTEM_MODULE_ENUMS = [
  "ADVANCED_FEATURES_MODULE",
  "CLIENTS_MODULE",
  "GROUP_MODULE",
  "MANAGEMENT_MODULE",
];

const MODULE_IMPORT_PRESETS = [
  { label: "Advanced", enums: ADVANCED_MODULE_ENUMS },
  { label: "Professional", enums: PROFESSIONAL_MODULE_ENUMS },
  { label: "Group System", enums: GROUP_SYSTEM_MODULE_ENUMS },
];

const LANGUAGES = [
  { code: "en", label: "English" },
  { code: "es", label: "Español" },
  { code: "fr", label: "Français" },
  { code: "de", label: "Deutsch" },
  { code: "it", label: "Italiano" },
  { code: "pt", label: "Português" },
  { code: "pt-BR", label: "Português BR" },
  { code: "nl", label: "Nederlands" },
  { code: "pl", label: "Polski" },
  { code: "ru", label: "Русский" },
  { code: "ja", label: "日本語" },
  { code: "zh-Hans", label: "简体中文" },
  { code: "zh-Hant", label: "繁體中文" },
  { code: "ko", label: "한국어" },
  { code: "ar", label: "العربية" },
  { code: "he", label: "עברית" },
  { code: "tr", label: "Türkçe" },
  { code: "th", label: "ไทย" },
  { code: "vi", label: "Tiếng Việt" },
  { code: "id", label: "Bahasa Indonesia" },
  { code: "fil", label: "Filipino" },
  { code: "sv", label: "Svenska" },
  { code: "no", label: "Norsk" },
  { code: "da", label: "Dansk" },
  { code: "fi", label: "Suomi" },
  { code: "cs", label: "Čeština" },
  { code: "hu", label: "Magyar" },
  { code: "ro", label: "Română" },
  { code: "bg", label: "Български" },
  { code: "hr", label: "Hrvatski" },
  { code: "el", label: "Ελληνικά" },
  { code: "sk", label: "Slovenčina" },
  { code: "sl", label: "Slovenščina" },
];
const TRANSLATE_BATCH_SIZE = 5;

// Google Translate's `tl` param uses different codes than ours for a few languages.
const GOOGLE_LANG_OVERRIDES = {
  "zh-Hans": "zh-CN",
  "zh-Hant": "zh-TW",
  "pt-BR": "pt",
  fil: "tl",
};

const translateText = async (text, targetLang) => {
  if (!text) return "";
  try {
    const googleLang = GOOGLE_LANG_OVERRIDES[targetLang] || targetLang;
    const res = await fetch(
      `https://translate.googleapis.com/translate_a/single?client=gtx&sl=en&tl=${googleLang}&dt=t&q=${encodeURIComponent(text)}`,
    );
    const data = await res.json();
    // data[0] is an array of [translatedSegment, originalSegment, ...] pairs
    return (data?.[0] || []).map((segment) => segment[0]).join("") || "";
  } catch (err) {
    console.error(`Error translating to ${targetLang}:`, err);
    return "";
  }
};

const initialFields = {
  name: "",
  subtitle: "",
  availability: "",
  price: "",
};

const AddDeviceModal = ({
  isOpen,
  onClose,
  distributorId,
  onSuccess,
  editProduct,
}) => {
  const isEditMode = !!editProduct;

  const [fields, setFields] = useState(initialFields);
  const [sameAsInventory, setSameAsInventory] = useState(false);
  const [availabilityCount, setAvailabilityCount] = useState("");
  const [availabilityDate, setAvailabilityDate] = useState("");
  const [modules, setModules] = useState([]);
  const [selectedModules, setSelectedModules] = useState([]);
  const [loadingModules, setLoadingModules] = useState(false);
  const [showImportModuleModal, setShowImportModuleModal] = useState(false);
  const [editorState, setEditorState] = useState(() =>
    EditorState.createEmpty(),
  );
  const [productImages, setProductImages] = useState([]);
  const [removeExistingImage, setRemoveExistingImage] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [confirmationEmail, setConfirmationEmail] = useState(null);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [translating, setTranslating] = useState(false);
  const [translateProgress, setTranslateProgress] = useState({
    done: 0,
    total: 0,
  });

  useEffect(() => {
    if (!isOpen) return;

    const fetchModules = async () => {
      setLoadingModules(true);
      try {
        const res = await fetch(`${API_URL}/get/all/modules`);
        const data = await res.json();
        const allModules = data?.modules || [];
        setModules(
          allModules.filter(
            (m) => !EXCLUDED_MODULE_ENUMS.includes(m.module_enum),
          ),
        );
      } catch (err) {
        console.error("Error fetching modules:", err);
      } finally {
        setLoadingModules(false);
      }
    };

    fetchModules();
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;

    if (editProduct) {
      setFields({
        name: editProduct.name || "",
        subtitle: editProduct.subtitle || "",
        availability: editProduct.availability || "",
        price: editProduct.price || "",
      });

      if (editProduct.description) {
        const blocksFromHtml = htmlToDraft(editProduct.description);
        const { contentBlocks, entityMap } = blocksFromHtml;
        const contentState = ContentState.createFromBlockArray(
          contentBlocks,
          entityMap,
        );
        setEditorState(EditorState.createWithContent(contentState));
      } else {
        setEditorState(EditorState.createEmpty());
      }

      if (
        editProduct.email_title ||
        editProduct.email_subject ||
        editProduct.email_text
      ) {
        setConfirmationEmail({
          title: editProduct.email_title || "",
          subject: editProduct.email_subject || "",
          text: editProduct.email_text || "",
        });
      } else {
        setConfirmationEmail(null);
      }

      setRemoveExistingImage(false);

      setSameAsInventory(editProduct.availability_mode === "inventory");
      setAvailabilityCount(
        editProduct.availability ? String(editProduct.availability) : ""
      );
      setAvailabilityDate(editProduct.availability_date || "");
    } else {
      setFields(initialFields);
      setEditorState(EditorState.createEmpty());
      setConfirmationEmail(null);
      setRemoveExistingImage(false);
      setSameAsInventory(false);
      setAvailabilityCount("");
      setAvailabilityDate("");
    }
  }, [isOpen, editProduct]);

  useEffect(() => {
    if (!editProduct || modules.length === 0) return;

    const moduleIds = (editProduct.module_id || "")
      .toString()
      .split(",")
      .filter(Boolean)
      .map((id) => id.trim());

    setSelectedModules(modules.filter((m) => moduleIds.includes(String(m.id))));
  }, [editProduct, modules]);

  const handleFieldChange = (e) => {
    const { name, value } = e.target;
    setFields((prev) => ({ ...prev, [name]: value }));
  };

  const handleImportModules = (preset) => {
    setSelectedModules(
      modules.filter((m) => preset.enums.includes(m.module_enum)),
    );
    setShowImportModuleModal(false);
  };

  const handleImageChange = (e) => {
    setProductImages(Array.from(e.target.files || []));
  };

  const removeImage = (name) => {
    setProductImages((prev) => prev.filter((img) => img.name !== name));
  };

  const resetForm = () => {
    setFields(initialFields);
    setSameAsInventory(false);
    setAvailabilityCount("");
    setAvailabilityDate("");
    setSelectedModules([]);
    setEditorState(EditorState.createEmpty());
    setProductImages([]);
    setRemoveExistingImage(false);
    setConfirmationEmail(null);
    setTranslating(false);
    setTranslateProgress({ done: 0, total: 0 });
    setError("");
  };

  // draft-js's getPlainText() joins every block (paragraph) with "\n". Translating
  // that whole blob as one string collapses all paragraph breaks in the result —
  // the translation API has no reason to preserve whitespace it wasn't asked to
  // treat as meaningful, so every non-English description came back as one flat
  // run of text with no <p> breaks. Translating paragraph-by-paragraph and
  // rejoining as separate <p> tags keeps each language's structure matching English.
  const translateToAllLanguages = async (sourceText) => {
    const paragraphs = sourceText
      .split(/\r?\n/)
      .map((line) => line.trim())
      .filter((line) => line.length > 0);

    const targets = LANGUAGES.filter((l) => l.code !== "en");
    const perLangParagraphs = {};
    targets.forEach((lang) => {
      perLangParagraphs[lang.code] = new Array(paragraphs.length).fill("");
    });

    const totalUnits = paragraphs.length * targets.length;
    let doneUnits = 0;
    setTranslating(true);
    setTranslateProgress({ done: 0, total: totalUnits });

    for (let p = 0; p < paragraphs.length; p++) {
      for (let i = 0; i < targets.length; i += TRANSLATE_BATCH_SIZE) {
        const batch = targets.slice(i, i + TRANSLATE_BATCH_SIZE);
        const results = await Promise.all(
          batch.map((lang) => translateText(paragraphs[p], lang.code)),
        );
        batch.forEach((lang, idx) => {
          perLangParagraphs[lang.code][p] = results[idx] || paragraphs[p];
        });
        doneUnits += batch.length;
        setTranslateProgress((prev) => ({
          ...prev,
          done: Math.min(doneUnits, totalUnits),
        }));
      }
    }

    setTranslating(false);

    const translations = { en: sourceText };
    targets.forEach((lang) => {
      translations[lang.code] = perLangParagraphs[lang.code]
        .filter(Boolean)
        .join("\n");
    });
    return translations;
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleSubmit = () => {
    setError("");

    if (!fields.name.trim()) return setError("Please enter a product name");
    // if (!fields.availability.trim()) return setError("Please enter availability");
    if (!fields.price || isNaN(fields.price))
      return setError("Please enter a valid price");

    if (!sameAsInventory && !availabilityCount)
      return setError(
        "Please select Availability same as product inventory or enter an availability count"
      );

    if (
      !sameAsInventory &&
      availabilityCount &&
      (isNaN(availabilityCount) || Number(availabilityCount) < 0)
    )
      return setError("Please enter a valid availability count");

    performSubmit();
  };

  const performSubmit = async () => {
    try {
      setLoading(true);

      const moduleIds = selectedModules.map((module) => module.id);
      const description = draftToHtml(
        convertToRaw(editorState.getCurrentContent()),
      );
      // const descriptionPlainText = editorState.getCurrentContent().getPlainText();
      const emailTitle = confirmationEmail?.title || "";
      const emailSubject = confirmationEmail?.subject || "";
      const emailText = confirmationEmail?.text || "";

      let descriptionTranslationsJson = "";
      // if (descriptionPlainText.trim()) {
      //   const translations = await translateToAllLanguages(descriptionPlainText);
      //   Object.keys(translations).forEach((code) => {
      //     if (code !== "en" && translations[code]) {
      //       // One <p> per original paragraph — matches the block structure
      //       // draftToHtml() produces for the English `description` below,
      //       // instead of collapsing every paragraph into one flat block.
      //       translations[code] = translations[code]
      //         .split(/\r?\n/)
      //         .map((line) => line.trim())
      //         .filter(Boolean)
      //         .map((line) => `<p>${line}</p>`)
      //         .join("");
      //     }
      //   });
      //   translations.en = description;
      //   descriptionTranslationsJson = JSON.stringify(translations);
      // }

      // The edit endpoint now parses multipart via formidable (same as add),
      // so both branches build the same kind of FormData — this also lets
      // edit accept a new image the same way add does.
      const formData = new FormData();
      formData.append("site_id", distributorId);
      formData.append("name", fields.name);
      formData.append("subtitle", fields.subtitle);
      formData.append(
        "availability",
        sameAsInventory ? 0 : availabilityCount || 0
      );
      formData.append(
        "availability_mode",
        sameAsInventory ? "inventory" : "count"
      );
      formData.append("availability_date", availabilityDate);
      formData.append("price", fields.price);
      formData.append("category", DEVICE_CATEGORY);
      formData.append("description", description);
      // formData.append("description_translations", descriptionTranslationsJson);
      formData.append("email_title", emailTitle);
      formData.append("email_subject", emailSubject);
      formData.append("email_text", emailText);
      moduleIds.forEach((id) => formData.append("module_id", id));
      productImages.forEach((image, i) => {
        formData.append(`form[${i}]`, image);
      });
      if (isEditMode && removeExistingImage) {
        formData.append("remove_image", "1");
      }

      const res = isEditMode
        ? await fetch(`${API_URL}/distributor/product/${editProduct.id}`, {
            method: "PUT",
            body: formData,
          })
        : await fetch(ADD_DEVICE_ENDPOINT, {
            method: "POST",
            body: formData,
          });

      const data = await res.json();

      if (!data.status) {
        throw new Error(data.message || "Something went wrong");
      }

      const imageRemoved =
        isEditMode && removeExistingImage && productImages.length === 0;
      resetForm();
      onSuccess && onSuccess({ imageRemoved });
      onClose();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const labelStyle = {
    display: "block",
    marginBottom: "6px",
    fontSize: "14px",
    fontWeight: "600",
    color: "rgb(77, 0, 77)",
  };
  const inputStyle = {
    width: "100%",
    padding: "10px 12px",
    border: "2px solid rgb(77, 0, 77)",
    borderRadius: "8px",
    fontSize: "14px",
    boxSizing: "border-box",
    color: "black",
  };

  return (
    <>
      <Modal isOpen={isOpen} toggle={handleClose} size="lg" centered>
        <ModalHeader toggle={handleClose} style={{ color: "#fff" }}>
          {isEditMode ? "Edit Product" : "Add Product"}
          {/* <small style={{ fontSize: "12px", color: "#e5e7eb", marginLeft: "8px" }}>
          {isEditMode ? "Update the product details below" : "Fill in the details below to add a new product"}
        </small> */}
        </ModalHeader>
        <ModalBody>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: "16px",
            }}
          >
            <div>
              <label style={labelStyle}>Name*</label>
              <input
                type="text"
                name="name"
                value={fields.name}
                onChange={handleFieldChange}
                placeholder="Enter product name"
                style={inputStyle}
              />
            </div>
            {/* <div>
            <label style={labelStyle}>Availability*</label>
            <input
              type="text"
              name="availability"
              value={fields.availability}
              onChange={handleFieldChange}
              placeholder="Enter product availability count"
              style={inputStyle}
            />
          </div> */}
            <div>
              <label style={labelStyle}>Price*</label>
              <input
                type="number"
                name="price"
                value={fields.price}
                onChange={handleFieldChange}
                placeholder="Enter product price"
                style={inputStyle}
              />
            </div>
          </div>

          <div
            style={{
              marginTop: "16px",
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: "12px",
            }}
          >
            <div>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  marginBottom: "6px",
                }}
              >
                <label style={{ ...labelStyle, fontSize: "12px", marginBottom: 0 }}>
                  Availability
                </label>
                <label
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                    fontSize: "12px",
                    fontWeight: "600",
                    color: "rgb(77, 0, 77)",
                    cursor: "pointer",
                    margin: 0,
                  }}
                >
                  <input
                    type="checkbox"
                    checked={sameAsInventory}
                    onChange={(e) => setSameAsInventory(e.target.checked)}
                  />
                  Same as inventory
                </label>
              </div>
              <input
                type="number"
                min="0"
                value={availabilityCount}
                onChange={(e) => setAvailabilityCount(e.target.value)}
                placeholder="Enter quantity"
                disabled={sameAsInventory}
                style={{
                  ...inputStyle,
                  ...(sameAsInventory
                    ? { background: "#e5e7eb", cursor: "not-allowed" }
                    : {}),
                }}
              />
            </div>
            <div>
              <label style={{ ...labelStyle, fontSize: "12px" }}>
                Available Until (optional)
              </label>
              <input
                type="date"
                value={availabilityDate}
                onChange={(e) => setAvailabilityDate(e.target.value)}
                style={inputStyle}
              />
            </div>
          </div>

          <div style={{ marginTop: "16px" }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                marginBottom: "6px",
              }}
            >
              <label style={{ ...labelStyle, marginBottom: 0 }}>
                Modules
                <span style={{ fontSize: "12px", fontWeight: "400", color: "#6b7280", marginLeft: "8px" }}>
                  (By default each product is a Basic System)
                </span>
              </label>
              <button
                type="button"
                onClick={() => setShowImportModuleModal(true)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  border: "none",
                  outline: "none",
                  boxShadow: "none",
                  borderRadius: "6px",
                  padding: "0",
                  fontSize: "12px",
                  fontWeight: "600",
                  color: "rgb(77, 0, 77)",
                  background: "white",
                  cursor: "pointer",
                }}
              >
                <i className="fa fa-copy"></i>
                Copy Modules
              </button>
            </div>
            {loadingModules ? (
              <p style={{ fontSize: "13px", color: "#6b7280" }}>
                Loading modules…
              </p>
            ) : (
              <Multiselect
                options={modules}
                selectedValues={selectedModules}
                displayValue="name"
                onSelect={setSelectedModules}
                onRemove={setSelectedModules}
                placeholder="Select modules to include"
              />
            )}
          </div>

          <div style={{ marginTop: "16px" }}>
            <label style={labelStyle}>Description</label>
            <div
              className="add-device-description-editor"
              style={{ border: "1px solid rgb(77, 0, 77)", borderRadius: "4px" }}
            >
              <style>{`
              .add-device-description-editor .add-device-editor-toolbar {
                border: none;
                border-bottom: 1px solid #e9d5ff;
                margin-bottom: 0;
              }
              .add-device-description-editor .add-device-editor-body {
                min-height: 120px;
                max-height: 160px;
                overflow-y: auto;
                padding: 0 10px;
              }
            `}</style>
              <Editor
                editorState={editorState}
                onEditorStateChange={setEditorState}
                wrapperClassName="add-device-editor-wrapper"
                editorClassName="add-device-editor-body"
                toolbarClassName="add-device-editor-toolbar"
              />
            </div>
          </div>

          {/* <p style={{ fontSize: "12px", color: "#6b7280", marginTop: "6px" }}>
          <i className="fas fa-language" style={{ marginRight: "4px" }}></i>
          On save, this description is automatically translated in to {LANGUAGES.length - 1} other languages.
          {translating && (
            <span style={{ marginLeft: "8px", color: "rgb(77, 0, 77)", fontWeight: "600" }}>
              Translating… {translateProgress.done}/{translateProgress.total}
            </span>
          )}
        </p> */}

          <div style={{ marginTop: "16px" }}>
            <label style={labelStyle}>Image</label>

            {isEditMode &&
              editProduct?.product_images &&
              !removeExistingImage &&
              productImages.length === 0 && (
                <div style={{ marginBottom: "8px" }}>
                  <div style={{ position: "relative", width: "80px" }}>
                    <img
                      src={`${PRODUCT_S3}/${editProduct.product_images}`}
                      alt={editProduct.name}
                      style={{
                        width: "80px",
                        height: "80px",
                        objectFit: "cover",
                        borderRadius: "8px",
                        border: "1px solid #e9d5ff",
                      }}
                      onError={(e) => {
                        e.target.style.display = "none";
                      }}
                    />
                    <span
                      onClick={() => setRemoveExistingImage(true)}
                      title="Remove image"
                      style={{
                        position: "absolute",
                        top: "-8px",
                        right: "-8px",
                        width: "20px",
                        height: "20px",
                        borderRadius: "50%",
                        background: "#dc2626",
                        color: "white",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: "11px",
                        cursor: "pointer",
                        boxShadow: "0 1px 3px rgba(0,0,0,0.3)",
                      }}
                    >
                      <i className="fa fa-times"></i>
                    </span>
                  </div>
                  <p
                    style={{
                      fontSize: "12px",
                      color: "#6b7280",
                      margin: "4px 0 0",
                    }}
                  >
                    Current image. Choose a new file below to replace it.
                  </p>
                </div>
              )}

            {isEditMode &&
              removeExistingImage &&
              productImages.length === 0 && (
                <p
                  style={{
                    fontSize: "12px",
                    color: "#dc2626",
                    marginBottom: "8px",
                  }}
                >
                  <i
                    className="fa fa-info-circle"
                    style={{ marginRight: "4px" }}
                  ></i>
                  Image will be removed when you save.
                </p>
              )}

            <input
              type="file"
              accept="image/*"
              onChange={handleImageChange}
              style={{
                width: "100%",
                border: "1px solid rgb(77, 0, 77)",
                padding: "0.5rem",
                borderRadius: "8px",
              }}
            />

            {productImages.length > 0 && (
              <div
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: "8px",
                  marginTop: "8px",
                }}
              >
                {productImages.map((img) => (
                  <span
                    key={img.name}
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "6px",
                      padding: "4px 8px",
                      borderRadius: "6px",
                      border: "1px solid rgb(77, 0, 77)",
                      fontSize: "12px",
                      color: "black",
                    }}
                  >
                    {img.name}
                    <i
                      className="fa fa-times"
                      style={{ cursor: "pointer" }}
                      onClick={() => removeImage(img.name)}
                    ></i>
                  </span>
                ))}
              </div>
            )}
          </div>

          <div
            style={{
              marginTop: "16px",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: "16px",
              padding: "12px 16px",
              border: "1px solid #e9d5ff",
              borderRadius: "8px",
              background: "#faf5ff",
            }}
          >
            <div>
              <p
                style={{
                  margin: 0,
                  fontSize: "14px",
                  fontWeight: "600",
                  color: "rgb(77, 0, 77)",
                }}
              >
                Confirmation Email
              </p>
              <p
                style={{
                  margin: "2px 0 0",
                  fontSize: "12px",
                  color: "#6b7280",
                }}
              >
                Create an email for your customer that tells them how to proceed
                with your product, e.g., a webinar <br />
                schedule. This email will accompany the order confirmation email
                that goes out to all customers.
              </p>
            </div>
            {confirmationEmail ? (
              <div style={{ display: "flex", gap: "8px", flexShrink: 0 }}>
                <button
                  type="button"
                  className="btn-secondary"
                  style={{ padding: "6px 14px", fontSize: "13px" }}
                  onClick={() => setShowEmailModal(true)}
                >
                  Edit
                </button>
                <button
                  type="button"
                  className="btn-secondary"
                  style={{
                    padding: "6px 14px",
                    fontSize: "13px",
                    color: "#dc2626",
                  }}
                  onClick={() => setConfirmationEmail(null)}
                >
                  Remove
                </button>
              </div>
            ) : (
              <button
                type="button"
                className="btn-primary"
                style={{ padding: "6px 14px", fontSize: "13px", flexShrink: 0 }}
                onClick={() => setShowEmailModal(true)}
              >
                Setup
              </button>
            )}
          </div>

          {error && (
            <div style={{ color: "red", fontSize: "12px", marginTop: "10px" }}>
              {error}
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          <button className="btn-secondary" onClick={handleClose}>
            Cancel
          </button>
          <button
            className="btn-primary"
            onClick={handleSubmit}
            disabled={loading}
          >
            {/* translating
            ? `Translating… ${translateProgress.done}/${translateProgress.total}`
            : */}

            {loading
              ? isEditMode
                ? "Saving..."
                : "Adding..."
              : isEditMode
                ? "Save Changes"
                : "Add Product"}
          </button>
        </ModalFooter>
      </Modal>

      <ConfirmationEmailModal
        isOpen={showEmailModal}
        onClose={() => setShowEmailModal(false)}
        initialValue={confirmationEmail}
        onSave={setConfirmationEmail}
      />

      <ImportModulePresetModal
        isOpen={showImportModuleModal}
        onClose={() => setShowImportModuleModal(false)}
        presets={MODULE_IMPORT_PRESETS}
        onSelect={handleImportModules}
      />
    </>
  );
};

export default AddDeviceModal;
